import { DropdownMenuItem, DropdownMenuSeparator } from '@components/atoms/DropdownMenu'
import { DotsMenu, type DotsMenuProps } from '@components/molecules/DotsMenu'
import Icon from '@components/utils/Icon'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<DotsMenuProps> = {
  ariaLabel: {
    type: 'string',
    required: true,
    description: 'Names the button for screen readers, for example "Actions for Maria Rossi".',
  },
  children: { type: 'ReactNode', required: true, description: 'The menu items. Use DropdownMenuItem and friends.' },
}

const doc: ComponentDoc<DotsMenuProps> = {
  slug: 'dots-menu',
  name: 'DotsMenu',
  tier: 'molecule',
  description: 'A ⋮ button that opens a menu of actions for one thing, such as a row or a card.',
  importStatement: "import { DotsMenu } from '@components/molecules/DotsMenu'",
  sourcePath: 'app/frontend/components/molecules/DotsMenu.tsx',
  component: DotsMenu,
  props,
  examples: [
    {
      title: 'Row actions',
      note: 'Put destructive items last, after a separator.',
      render: () => (
        <DotsMenu ariaLabel="Actions for How to send an SMS">
          <DropdownMenuItem>
            <Icon type="pencil-01" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Icon type="chevron-down" />
            Move down
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive>
            <Icon type="trash-01" />
            Delete
          </DropdownMenuItem>
        </DotsMenu>
      ),
      code: `<DotsMenu ariaLabel={\`Actions for \${faq.title}\`}>
  <DropdownMenuItem onSelect={edit}>
    <Icon type="pencil-01" />
    Edit
  </DropdownMenuItem>
  <DropdownMenuSeparator />
  <DropdownMenuItem destructive onSelect={confirmDelete}>
    <Icon type="trash-01" />
    Delete
  </DropdownMenuItem>
</DotsMenu>`,
    },
  ],
  usage: {
    do: [
      'Use it for the less common actions on a row or card.',
      'Mark delete and other destructive items with destructive.',
      'Ask before a destructive action runs. Open a ConfirmDialog from the item.',
      'Make an item a link (ModalLink, Inertia Link) with asChild when it goes somewhere. The menu closes on any item click.',
    ],
    dont: [
      'Hide the action people use most. Show it as a button beside the menu.',
      'Build your own ⋮ button. Use this so every menu looks the same.',
      'Use it when one action stands out. Use a SplitButton.',
    ],
  },
  a11y: [
    'The button has no text, so ariaLabel is required. Name the thing it acts on.',
    'The menu opens with Enter, Space or the down arrow. Escape closes it and returns focus to the button.',
  ],
}

export default doc
